import React, { useCallback, useContext, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { t, Trans } from "@lingui/macro";
import { useMutation } from "@apollo/client";
import {
  StripeCardCvcElementOptions,
  StripeCardExpiryElementOptions,
  StripeCardNumberElementOptions,
} from "@stripe/stripe-js";
import { CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import CreateStripeSubscriptionMutation from "../../../graphql/CreateStripeSubscription.graphql";
import UpdateStripePaymentMethodMutation from "../../../graphql/UpdateStripePaymentMethod.graphql";
import {
  MembershipSubscriptionProvider,
  StripeCurrency,
  StripeSubscriptionInput,
  StripeSubscriptionPlan,
  UpdateStripePaymentMethodInput,
} from "../../../types/graphql-global-types";
import { getLocale } from "../../../utils/getLocale";
import { getWelcomeURL } from "../utils/getWelcomeURL";
import { getContentOrigin } from "../utils/getContentOrigin";
import { getPlanOrigin } from "../utils/getPlanOrigin";
import { SubscribeContinueBlackButton } from "./SubscribeContinueBlackButton";
import { sendEvent } from "../../../utils/gtm";
import { LoginContext } from "../../../components/LoginProvider/LoginProvider";
import { PaymentContext } from "../contexts/PaymentProvider";
import CardNumberInput from "./CardNumberInput";
import CardExpiryInput from "./CardExpiryInput";
import CardCvcInput from "./CardCvcInput";
import { Locale } from "../../../types/Locale";
import useNameInputsWithUpdate from "../hooks/useNameInputsWithUpdate";
import useGeoCountryCode from "../../../graphql/useGeoCountryCode/useGeoCountryCode";
import { isUttLoaded, sendConversionToImpact } from "../utils/impact";
import { ImpactPayLoad } from "../../../types/ImpactTracking";

const StripeSubscribeSection = ({
  updateName,
  postChildren,
  disclaimer,
  discount,
}: {
  updateName?: boolean;
  postChildren?: React.ReactNode;
  disclaimer?: string;
  discount?: boolean;
}) => {
  const { user } = useContext(LoginContext);

  const {
    selectedPlan,
    selectedMethod,
    isReady,
    setPaymentError,
    setPaymentLoading,
    subscription,
    pollMembershipStatus,
  } = useContext(PaymentContext);

  const { isNameInputValid, handleUpdateName, render } = useNameInputsWithUpdate({
    isDisabled: !isReady,
  });

  const { data: geoCountryCode } = useGeoCountryCode();

  const visible =
    (selectedMethod === "STRIPE" || subscription?.provider === MembershipSubscriptionProvider.STRIPE) &&
    subscription?.provider !== MembershipSubscriptionProvider.PAYPAL;

  const tt = useTranslations();
  const locale = getLocale();

  const contentOrigin = getContentOrigin();
  const planOrigin = getPlanOrigin();

  const [stripeElementsOptions, setStripeElementsOptions] = useState<
    StripeCardNumberElementOptions | StripeCardExpiryElementOptions | StripeCardCvcElementOptions
  >({
    style: {
      base: {
        iconColor: "rgba(0, 0, 0, 0.75)",
        fontSize: "18px",
        fontWeight: "400",
        fontFamily: "Diet Doctor Sans, Trebuchet MS, Arial, sans-serif",
      },
    },
    disabled: false,
  });

  const [createStripeSubscription] = useMutation(CreateStripeSubscriptionMutation);
  const [updateStripePaymentMethod] = useMutation(UpdateStripePaymentMethodMutation);

  const stripe = useStripe();
  const elements = useElements();

  // Handle payment loading Stripe card input disable
  useEffect(() => {
    if (!isReady) {
      setStripeElementsOptions({ ...stripeElementsOptions, disabled: true });
    } else {
      setStripeElementsOptions({ ...stripeElementsOptions, disabled: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  // When card info submitted
  const handleStripeElements = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      // Block native form submission.
      event?.preventDefault();

      if (
        !stripe ||
        !elements ||
        !document.querySelector("#card-number-element")?.classList.contains("StripeElement--complete") ||
        !document.querySelector("#card-expiry-element")?.classList.contains("StripeElement--complete") ||
        !document.querySelector("#card-cvc-element")?.classList.contains("StripeElement--complete") ||
        !isReady
      ) {
        // Stripe.js has not yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return null;
      }

      if (
        subscription?.provider !== MembershipSubscriptionProvider.STRIPE &&
        typeof subscription?.provider !== "undefined"
      ) {
        return null;
      }

      setPaymentError(false);
      setPaymentLoading(`${new Date().getTime() / 1000}`);

      let firstName = user?.firstName;
      let lastName = user?.lastName;

      if (updateName) {
        try {
          const { firstName: newFirstName, lastName: newLastName } = await handleUpdateName();
          firstName = newFirstName;
          lastName = newLastName;
        } catch (error) {
          setPaymentLoading("false");
          return console.log("handleUpdateName error", error);
        }
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement)!,
        billing_details: {
          email: user?.email,
          name: `${firstName} ${lastName}`,
          address: {
            country: geoCountryCode ?? undefined,
          },
        },
      });

      if (error) {
        setPaymentError(error.message || true);
        setPaymentLoading("false");
        return;
      } else {
        const paymentMethodId = paymentMethod!.id;

        const payload: StripeSubscriptionInput = {
          paymentMethodId: paymentMethodId,
          plan: selectedPlan === "ANNUAL" ? StripeSubscriptionPlan.YEARLY : StripeSubscriptionPlan.MONTHLY,
          currency:
            locale === Locale.SV
              ? StripeCurrency.SEK
              : locale === Locale.ES
              ? StripeCurrency.USD_ES
              : StripeCurrency.USD,
          trialPeriodDays: Number(process.env.STRIPE_TRIAL_PERIOD_DAYS) || 7,
          d: discount ? 45 : 0,
        };

        const purchaseMembershipEvent = {
          eventName: "signup",
          action: "purchase_membership",
          payload: {
            success: true,
            locale: locale!,
            payment_method: "stripe",
            plan: selectedPlan,
            trial_period_days: Number(process.env.STRIPE_TRIAL_PERIOD_DAYS) || 7,
          },
        } as const;

        try {
          // Attempt to create subscription
          await createStripeSubscription({
            variables: {
              input: payload,
            },
          });

          const result = await pollMembershipStatus(true);
          if (result === true) {
            setPaymentLoading("false");
            sendEvent(purchaseMembershipEvent);
            if (isUttLoaded() && user?.userId && user?.email) {
              const impactPayload: ImpactPayLoad = {
                id: paymentMethod.id,
                userId: user?.userId,
                email: user?.email,
                customerStatus: "SUCCESS",
                plan: selectedPlan,
              };

              sendConversionToImpact(impactPayload);
            }

            return (window.location.href = getWelcomeURL(planOrigin || selectedPlan)!);
          }

          // 3D Secure handle
          if (result && result.status === "incomplete") {
            try {
              const confirmCardPaymentResult = await stripe!.confirmCardSetup(
                result.clientSecret!,
                {
                  payment_method: paymentMethodId,
                },
                {
                  handleActions: true,
                }
              );

              if (confirmCardPaymentResult.error) {
                setPaymentError(confirmCardPaymentResult.error.message || true);
                setPaymentLoading("false");
              } else {
                if (confirmCardPaymentResult.setupIntent!.status === "succeeded") {
                  // There's a risk of the customer closing the window before the callback.
                  const payload: UpdateStripePaymentMethodInput = {
                    paymentMethodId: confirmCardPaymentResult.setupIntent!.payment_method! as string,
                  };
                  await updateStripePaymentMethod({
                    variables: {
                      input: payload,
                    },
                  });

                  try {
                    const result = await pollMembershipStatus();
                    if (result === true) {
                      setPaymentLoading("false");
                      sendEvent(purchaseMembershipEvent);
                      if (isUttLoaded() && user?.userId && user?.email) {
                        const impactPayload: ImpactPayLoad = {
                          id: paymentMethod.id,
                          userId: user?.userId,
                          email: user?.email,
                          customerStatus: "SUCCESS",
                          plan: selectedPlan,
                        };

                        sendConversionToImpact(impactPayload);
                      }
                      return (window.location.href = getWelcomeURL(planOrigin || selectedPlan)!);
                    }
                  } catch (error) {
                    throw Error("Couldn't find active Stripe subscription");
                  }
                }
              }
            } catch (error) {
              console.log("confirmCardPayment error", error);
            }
          }
        } catch (error: any) {
          let errorValue: string | boolean = true;
          if (error === "expired_card") {
            errorValue = "Your card has expired. Please enter correct expiry date and try again.";
          }

          if (error === "incorrect_cvc") {
            errorValue =
              "Your card's security code is incorrect. Please enter correct security code and try again.";
          }
          sendEvent({
            ...purchaseMembershipEvent,
            payload: {
              ...purchaseMembershipEvent.payload,
              success: false,
            },
          });
          setPaymentError(errorValue);
          setPaymentLoading("false");
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      stripe,
      elements,
      user,
      selectedPlan,
      locale,
      createStripeSubscription,
      pollMembershipStatus,
      contentOrigin,
      updateStripePaymentMethod,
    ]
  );

  return (
    <div className={`${visible ? "" : "hidden"}`}>
      {elements && (
        <StyledStripeElements
          onSubmit={handleStripeElements}
          className="flex flex-col items-start mx-auto mt-6"
          id="stripe-elements"
        >
          {updateName && render}

          <CardNumberInput
            elements={elements}
            stripeElementsOptions={stripeElementsOptions}
            tt={tt}
            disableAutoFocus={updateName ? true : false}
          />

          <div className="flex flex-row w-full mt-3">
            <CardExpiryInput stripeElementsOptions={stripeElementsOptions} tt={tt} />

            <CardCvcInput stripeElementsOptions={stripeElementsOptions} />
          </div>

          {postChildren}

          <p
            className={`my-6 p-0 opacity-75 text-left md:mx-auto text-sm ${disclaimer ? "" : "md:text-center"}`}
          >
            {disclaimer
              ? disclaimer
              : selectedPlan === "ANNUAL"
              ? tt.willNotBeChargedYearly
              : tt.willNotBeChargedMonthly}
          </p>

          <SubscribeContinueBlackButton
            type="submit"
            disabled={!stripe || updateName ? !isNameInputValid : undefined}
            loading={!isReady}
            hexColor={"#06B05B"}
            data-testid="pay"
            onClick={(event) => {
              if (!isReady) {
                event.preventDefault();
              }
            }}
          >
            {tt.startMyFreeTrial}
          </SubscribeContinueBlackButton>
        </StyledStripeElements>
      )}
    </div>
  );
};

export default React.memo(StripeSubscribeSection);

function useTranslations() {
  return {
    cardNumber: t({
      id: "StripeElements.cardNumber",
      message: "Card number",
    }),
    expiryDate: t({
      id: "StripeElements.expiryDate",
      message: "Expiry date (MM/YY)",
    }),
    CVV: t({
      id: "StripeElements.CVV",
      message: "CVV",
    }),
    willNotBeChargedMonthly: (
      <Trans id="StripeElements.willNotBeChargedMonthly">
        You will not be charged until your free trial ends.
        <br />
        If you don't cancel, you will be charged US$14.99 per month.{" "}
        <span className="hidden md:inline">
          <br />
        </span>
        You can cancel anytime and pay nothing.
      </Trans>
    ),
    willNotBeChargedYearly: (
      <Trans id="StripeElements.willNotBeChargedYearly">
        You will not be charged until your free trial ends.
        <br />
        If you don't cancel, you will be charged US$119 per year.{" "}
        <span className="hidden md:inline">
          <br />
        </span>
        You can cancel anytime and pay nothing.
      </Trans>
    ),
    startMyFreeTrial: t({
      id: "StripeElements.startMyFreeTrial",
      message: "Start my free trial",
    }),
  };
}

const StyledStripeElements = styled.form`
  .StripeElement {
    height: 56px;
    transition: box-shadow 0.08s ease-in, color 0.08s ease-in, filter 50000s;
    display: flex;
    flex-direction: column;
    justify-content: center;
    max-width: 500px;
    outline: 0;
    background: white;
    font-family: Diet Doctor Sans, Trebuchet MS, Arial, sans-serif;
    border-width: 1px;
    border-radius: 0.5rem;
    border-color: rgb(151 151 151);
    padding-left: 0.875rem;
    padding-right: 0.875rem;
    padding-top: 1.5rem;
    padding-bottom: 0.5rem;
    font-size: 1.125rem;
    line-height: 1.75rem;
  }

  .StripeElement::placeholder {
    color: #aab7c4;
  }

  .StripeElement--focus {
    transition: all 0.08s ease-in;
    border-color: rgba(21, 91, 113, 0.75);
    box-shadow: 0 0 0 1px rgba(21, 91, 113, 0.75);
    transition: box-shadow 0.05s ease-in, color 0.08s ease-in;
  }

  .StripeElement--focus ~ label {
    transform: scale(0.7) translateY(-0.875rem);
  }

  .StripeElement--invalid {
    border-color: transparent;
    background-color: rgba(255, 191, 191, 0.2) !important;
    box-shadow: 0 0 0 2px rgba(228, 21, 21, 0.75);
    transition: box-shadow 0.08s ease-in, color 0.08s ease-in;
  }
`;
